if (!customElements.get('live-metal-price-bar')) {
  const TREND_ICONS = {
    up: '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 1.5L8.5 6.5H1.5L5 1.5Z" fill="currentColor"/></svg>',
    down: '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 8.5L1.5 3.5H8.5L5 8.5Z" fill="currentColor"/></svg>',
  };

  const STORAGE_KEY = 'live-metal-price-currency';
  const SUPPORTED_CURRENCIES = ['AUD', 'EUR', 'USD'];
  const DEFAULT_CURRENCY = 'AUD';
  const PRICE_FORMATTER = new Intl.NumberFormat('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const STOREFRONT_QUERY = `
    query LiveMetalPrices {
      shop {
        metafield(namespace: "custom", key: "metal_price") {
          value
        }
      }
    }
  `;

  class LiveMetalPriceBar extends HTMLElement {
    connectedCallback() {
      this.storefrontApiToken = this.dataset.storefrontApiToken;
      this.storefrontApiVersion = this.dataset.storefrontApiVersion || '2026-07';
      this.storefrontDomain = window.Shopify?.shop || window.location.hostname;
      this.refreshInterval = Number(this.dataset.refreshInterval) || 60000;
      this.updatedAt = this.dataset.updatedAt || '';
      this.currencyData = this.parseCurrencyData();
      this.selectedCurrency = this.getStoredCurrency();

      this.handleCurrencyChange = this.onCurrencyChange.bind(this);
      this.addEventListener('change', this.handleCurrencyChange);

      const select = this.querySelector('[data-currency-select]');
      if (select) {
        select.value = this.selectedCurrency;
      }

      this.applyCurrency(this.selectedCurrency);

      if (!this.storefrontApiToken || !this.storefrontDomain || this.refreshInterval <= 0) return;

      this.refreshHandler = this.refresh.bind(this);
      this.intervalId = window.setInterval(this.refreshHandler, this.refreshInterval);

      this.handleVisibilityChange = () => {
        if (document.hidden) {
          window.clearInterval(this.intervalId);
          this.intervalId = null;
          return;
        }

        this.refresh();
        if (!this.intervalId) {
          this.intervalId = window.setInterval(this.refreshHandler, this.refreshInterval);
        }
      };

      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    disconnectedCallback() {
      window.clearInterval(this.intervalId);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.removeEventListener('change', this.handleCurrencyChange);
    }

    parseCurrencyData() {
      const jsonEl = this.querySelector('[data-metal-prices-json]');
      if (!jsonEl) return null;

      try {
        return JSON.parse(jsonEl.textContent);
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
        return null;
      }
    }

    getStoredCurrency() {
      try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return SUPPORTED_CURRENCIES.includes(stored) ? stored : DEFAULT_CURRENCY;
      } catch {
        return DEFAULT_CURRENCY;
      }
    }

    storeCurrency(currency) {
      try {
        window.localStorage.setItem(STORAGE_KEY, currency);
      } catch {
        // Ignore storage errors.
      }
    }

    getMetalsForCurrency(currency) {
      if (!this.currencyData) return null;

      if (this.currencyData.currencies?.[currency]?.metals) {
        return this.currencyData.currencies[currency].metals;
      }

      if (currency === DEFAULT_CURRENCY) {
        return this.currencyData.metals || null;
      }

      return null;
    }

    onCurrencyChange(evt) {
      if (!evt.target.matches('[data-currency-select]')) return;
      this.applyCurrency(evt.target.value);
    }

    applyCurrency(currency) {
      const nextCurrency = SUPPORTED_CURRENCIES.includes(currency) ? currency : DEFAULT_CURRENCY;
      this.selectedCurrency = nextCurrency;
      this.storeCurrency(nextCurrency);

      const select = this.querySelector('[data-currency-select]');
      if (select && select.value !== nextCurrency) {
        select.value = nextCurrency;
      }

      const metals = this.getMetalsForCurrency(nextCurrency);
      if (!metals) return;

      ['gold', 'silver', 'platinum'].forEach((metal) => {
        const row = this.querySelector(`[data-metal="${metal}"]`);
        const metalData = metals[metal];
        if (!row || !metalData) return;

        const priceEl = row.querySelector('[data-metal-price]');
        const trendEl = row.querySelector('[data-metal-trend]');

        if (priceEl && metalData.price != null) {
          const numericPrice = Number(String(metalData.price).replace(/,/g, ''));
          priceEl.textContent = Number.isFinite(numericPrice)
            ? PRICE_FORMATTER.format(numericPrice)
            : metalData.price;
        }

        if (trendEl) {
          const trend = metalData.trend || 'none';
          trendEl.dataset.trend = trend;
          trendEl.classList.remove('live-metal-price__trend--up', 'live-metal-price__trend--down');
          trendEl.hidden = trend === 'none';

          if (trend === 'up') {
            trendEl.classList.add('live-metal-price__trend--up');
            trendEl.innerHTML = TREND_ICONS.up;
          } else if (trend === 'down') {
            trendEl.classList.add('live-metal-price__trend--down');
            trendEl.innerHTML = TREND_ICONS.down;
          } else {
            trendEl.innerHTML = '';
          }
        }
      });
    }

    async refresh() {
      if (this.isRefreshing) return;
      this.isRefreshing = true;

      try {
        const response = await fetch(
          `https://${this.storefrontDomain}/api/${this.storefrontApiVersion}/graphql.json`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': this.storefrontApiToken,
            },
            body: JSON.stringify({ query: STOREFRONT_QUERY }),
          }
        );

        if (!response.ok) throw new Error(response.status);

        const result = await response.json();
        if (result.errors?.length) throw new Error(result.errors[0].message);

        const metafieldValue = result.data?.shop?.metafield?.value;
        if (!metafieldValue) return;

        const nextCurrencyData = JSON.parse(metafieldValue);
        const nextUpdatedAt = nextCurrencyData.updated_at || '';
        if (nextUpdatedAt && nextUpdatedAt === this.updatedAt) return;

        this.currencyData = nextCurrencyData;
        this.updatedAt = nextUpdatedAt;

        let jsonEl = this.querySelector('[data-metal-prices-json]');
        if (!jsonEl) {
          jsonEl = document.createElement('script');
          jsonEl.type = 'application/json';
          jsonEl.setAttribute('data-metal-prices-json', '');
          this.insertBefore(jsonEl, this.firstChild);
        }
        jsonEl.textContent = metafieldValue;

        if (nextUpdatedAt) this.dataset.updatedAt = nextUpdatedAt;

        this.applyCurrency(this.selectedCurrency);
      } catch (error) {
        console.log(error); // eslint-disable-line no-console
      } finally {
        this.isRefreshing = false;
      }
    }
  }

  customElements.define('live-metal-price-bar', LiveMetalPriceBar);
}
